import type React from "react"
import { useCallback, useEffect, useState } from "react"
import { gettext } from "@multimediallc/web-utils"
import { useAppContext } from "../../../hooks/appContext"
import { Sizes, Weights } from "../../../interfaces/sizing"
import { Button, ButtonColor, Typography } from "../../common"
import "./DateForm.scss"

const i18n = {
    pleaseSelectDateText: gettext("Please select a date to view"),
    submitText: gettext("Submit"),
}

interface DateFormProps {
    /** Function triggered when the form is submitted */
    onFormSubmit: (date: string) => void
    /** The default selected date (in YYYY-M-D format) */
    initialDate?: string
}

export function DateForm({ onFormSubmit, initialDate }: DateFormProps) {
    // Day, Month and Year should be defaulted to today
    const [selectedDay, setSelectedDay] = useState<string>(() => {
        const initialDay = initialDate
            ? parseInt(initialDate.split("-")[2])
            : undefined
        return String(initialDay ?? new Date().getDate())
    })
    const [selectedMonth, setSelectedMonth] = useState<string>(() => {
        const initialMonth = initialDate
            ? parseInt(initialDate.split("-")[1])
            : undefined
        return String(initialMonth ?? new Date().getMonth() + 1)
    })
    const [selectedYear, setSelectedYear] = useState<string>(() => {
        const initialYear = initialDate
            ? parseInt(initialDate.split("-")[0])
            : undefined
        return String(initialYear ?? new Date().getFullYear())
    })
    const languageCode = useAppContext().context.language_code

    /** Get days in month based off the selected year and month */
    const getDaysInMonth = useCallback(() => {
        let daysInMonth = 31
        const year = parseInt(selectedYear)
        const month = parseInt(selectedMonth)

        const isLeapYear =
            (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0

        if (month === 2) {
            if (isLeapYear) {
                daysInMonth = 29
            } else {
                daysInMonth = 28
            }
        } else if (month === 4 || month === 6 || month === 9 || month === 11) {
            daysInMonth = 30
        }

        return daysInMonth
    }, [selectedYear, selectedMonth])

    /** Gets the date in YYYY-M-D format. */
    const getFormattedDate = (): string => {
        return `${selectedYear}-${selectedMonth}-${selectedDay}`
    }

    /** Handle form submit */
    const handleSubmit = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ): void => {
        event.preventDefault()
        onFormSubmit(getFormattedDate())
    }

    /** Generate options for dates based on selected month and year.
    This function also includes handling leap years */
    const generateDateOptions = (): JSX.Element[] => {
        const daysInMonth = getDaysInMonth()

        return Array.from({ length: daysInMonth }, (_, index) => index + 1).map(
            (day) => (
                <option key={day} value={day}>
                    {day}
                </option>
            ),
        )
    }

    /** Generate options for years from current year to 2010 */
    const generateYearOptions = (): JSX.Element[] => {
        const currentYear = new Date().getFullYear()
        const years = Array.from(
            { length: currentYear - 2010 + 1 },
            (_, index) => currentYear - index,
        )
        return years.map((year) => (
            <option key={year} value={year}>
                {year}
            </option>
        ))
    }

    /** Generates the month option with a text format (January, February, etc) */
    const generateMonthOptions = (): JSX.Element[] => {
        const months = Array.from({ length: 12 }, (_, index) => index + 1)
        return months.map((month) => {
            const monthName = new Date(0, month - 1).toLocaleString(
                languageCode,
                {
                    month: "long",
                },
            )
            return (
                <option key={month} value={month}>
                    {monthName}
                </option>
            )
        })
    }

    useEffect(() => {
        // If the selected day is more than the total days in the selected month
        // then set the day to the last day of that month
        const day = parseInt(selectedDay)
        const daysInMonth = getDaysInMonth()
        if (day > daysInMonth) {
            setSelectedDay(String(daysInMonth))
        }
    }, [selectedDay, getDaysInMonth])

    return (
        <form className="DateForm">
            <Typography
                size={Sizes.xs}
                weight={Weights.bold}
                className="formLabel"
            >
                {i18n.pleaseSelectDateText}:
            </Typography>
            <div className="selectContainer">
                <select
                    aria-label="month"
                    id="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                >
                    {generateMonthOptions()}
                </select>
                <select
                    aria-label="date"
                    id="date"
                    value={selectedDay}
                    onChange={(e) => setSelectedDay(e.target.value)}
                >
                    {generateDateOptions()}
                </select>
                <select
                    aria-label="year"
                    id="year"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                >
                    {generateYearOptions()}
                </select>
            </div>
            <Button
                onClick={(e) => handleSubmit(e)}
                text={i18n.submitText}
                color={ButtonColor.Orange}
            />
        </form>
    )
}
